import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { CirclePlus, PersonPlus, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal, Sheet, TextInput, Tooltip } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAddEstablishmentMutation } from 'store/api';

import { ADD_ESTABLISHMENT, CANCEL, ENTER_ESTABLISHMENT_NAME, ESTABLISHMENT, SAVE } from '../../constants';
import type { EstablishmentAddForm } from '../utils/useValidation';
import { useEstablishmentAddValidation } from '../utils/useValidation';

import styles from './styles.module.scss';

export const EstablishmentSheetAdd = ({ onAdd, isAdaptive }: { onAdd: () => void; isAdaptive: any }) => {
	const [open, setOpen] = useState(false);

	const [addEstablishment] = useAddEstablishmentMutation();

	const { establishmentAddSchema } = useEstablishmentAddValidation();

	const methods = useForm<EstablishmentAddForm>({
		resolver: zodResolver(establishmentAddSchema),
		defaultValues: {
			name: '',
		},
	});

	const {
		handleSubmit,
		register,
		reset,
		formState: { errors, isSubmitted, isValid },
	} = methods;

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit = async (data: EstablishmentAddForm) => {
		try {
			await addEstablishment(data).unwrap();
			onAdd();
			setOpen(false);
			toast.success(
				'Учреждение успешно добавлено Новое учреждение сохранено и доступно в общем списке учреждений',
			);
			reset();
		} catch (error: any) {
			toast.error(error.data.message);
		}
	};

	return (
		<>
			<Button className={styles.btn} size="l" onClick={() => setOpen(true)} view="flat">
				<Icon data={CirclePlus} size={20} />
			</Button>

			<Sheet visible={open} onClose={() => setOpen(false)} className={styles.sheet} title={ADD_ESTABLISHMENT}>
				<div className={styles.sheet__close}>
					<Button onClick={() => setOpen(false)} view="flat">
						<Icon data={Xmark} size={16} />
					</Button>
				</div>

				<div>
					<FormProvider {...methods}>
						<form onSubmit={handleSubmit(onSubmit)} className={styles.modal__rows}>
							<FormRow className={styles.modal__row} required label={ESTABLISHMENT}>
								<TextInput
									size="xl"
									placeholder={ENTER_ESTABLISHMENT_NAME}
									hasClear
									{...register('name', { required: true })}
									validationState={isSubmitted && errors.name ? 'invalid' : undefined}
									errorPlacement="inside"
									errorMessage={isSubmitted && errors.name ? 'Обязательно для заполнения' : undefined}
								/>
							</FormRow>
							<div className={styles.modal__btns}>
								<Button size="xl" view="flat-secondary" onClick={() => setOpen(false)}>
									{CANCEL}
								</Button>
								<Tooltip content="Введите название" disabled={isValid} openDelay={100} placement="top">
									<div tabIndex={0}>
										<Button size="xl" type="submit" className={styles.modal__btns__save}>
											{SAVE}
										</Button>
									</div>
								</Tooltip>
							</div>
						</form>
					</FormProvider>
				</div>
			</Sheet>
		</>
	);
};
